/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  OnDestroy,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';

import {Subject} from 'rxjs';

import {FormTabRowComponent, GroupZoneControlGroup} from './form-tab-row/form-tab-row.component';

import {TimeAndAttendanceComponent} from '../../../+payrolls/prepare/time/time-and-attendance.component';
import {VariableElementsComponent} from '../../../+payrolls/prepare/variable-elements/variable-elements.component';
import {Employee} from '../../../shared/employees/employees.model';
import {ChangeTrackingComponent} from '../../change-tracking/change-tracking.component';
import {ContractAlertsComponent} from '../../contract-alerts/contract-alerts.component';
import {DocumentsListComponent} from '../../documents/documents-list/documents-list.component';
import {AttachmentsComponent} from '../../employees/attachments.component';
import {EmployeePayslipsComponent} from '../../employees/employee-payslips.component';
import {LogEventComponent} from '../../log-event/log-event.component';
import {PayrollInputAdminComponent} from '../../sdconnect/payroll-input-admin/payroll-input-admin.component';
import {
  PersonMasterDataAdminComponent
} from '../../sdconnect/person-master-data-admin/person-master-data-admin.component';
import {SdcPersonFormComponent} from '../../sdconnect/sdc-person-form/sdc-person-form.component';
import {FormValues, GroupZoneDescriptor, TabSheetDescriptor} from '../form-generator.interfaces';

/**
 * Tab component for the form generator.
 * Holds a tab that can be active or inactive, that has a name and a list of cards
 */
@Component({
  selector: 'gp-form-tab',
  template: `
    <div [attr.data-hidden]="!active" class="tab-pane-content p-t-md">
      <div #rowsContainer></div>
      <!-- Card rows injected here -->
    </div>
  `,
  styleUrls: ['form-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormTabComponent implements AfterViewInit, OnDestroy {

  public descriptor: TabSheetDescriptor;

  public group: UntypedFormGroup;
  public locale: string;
  public currency: string;
  public cliRegID: number = null;
  public data: FormValues;

  public loaded: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();
  public readOnly: boolean = false;
  public rows: Array<ComponentRef<FormTabRowComponent>> = [];

  public paySlipComponent: EmployeePayslipsComponent;
  public changeTrackingComponent: ChangeTrackingComponent;
  public variableElementsComponent: VariableElementsComponent;
  public timeAndAttendanceComponent: TimeAndAttendanceComponent;
  public attachmentsComponent: AttachmentsComponent;
  public logEventComponent: LogEventComponent;
  public contractAlertsComponent: ContractAlertsComponent;
  public documentsComponent: DocumentsListComponent;
  public personMasterDataAdminComponent: PersonMasterDataAdminComponent;
  public payrollInputAdminComponent: PayrollInputAdminComponent;
  public sdcPersonFormComponent: SdcPersonFormComponent;

  public groupedCards: any;
  public form: UntypedFormGroup;
  public activeCardSubject: Subject<boolean>;
  public isOnBoarding = false;
  public employee: Employee = null;
  public selectedOrg = null;

  private _active: boolean = false;
  set active(active: boolean) {
    this.cd.markForCheck();
    this._active = active;
  }

  get active() {
    return this._active;
  }

  @ViewChild('rowsContainer', {read: ViewContainerRef, static: true})
  private rowsContainer: ViewContainerRef;

  constructor(private componentFactoryResolver: ComponentFactoryResolver,
              private formBuilder: UntypedFormBuilder,
              private cd: ChangeDetectorRef) {
  }

  public ngAfterViewInit() {
    if (this.descriptor.tabSheetID === 'Payslips') {
      const ref: ComponentRef<EmployeePayslipsComponent> = this.rowsContainer.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(EmployeePayslipsComponent));
      this.paySlipComponent = ref.instance;
    }

    if (this.descriptor.tabSheetID === 'VariableElements') {
      const ref: ComponentRef<VariableElementsComponent> = this.rowsContainer.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(VariableElementsComponent));
      this.variableElementsComponent = ref.instance;
    }

    if (this.descriptor.tabSheetID === 'Time') {
      const ref: ComponentRef<TimeAndAttendanceComponent> = this.rowsContainer.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(TimeAndAttendanceComponent));
      this.timeAndAttendanceComponent = ref.instance;
    }


    if (this.descriptor.tabSheetID === 'ChangeTracking') {
      const ref: ComponentRef<ChangeTrackingComponent> = this.rowsContainer.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(ChangeTrackingComponent));
      this.changeTrackingComponent = ref.instance;
    }


    if (this.descriptor.tabSheetID === 'Attachments') {
      const ref: ComponentRef<AttachmentsComponent> = this.rowsContainer.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(AttachmentsComponent));
      this.attachmentsComponent = ref.instance;
      this.attachmentsComponent.isReadOnly = this.readOnly;
    }


    if (this.descriptor.tabSheetID === 'EventsLog') {
      const ref: ComponentRef<LogEventComponent> = this.rowsContainer.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(LogEventComponent));
      this.logEventComponent = ref.instance;
    }

    if (this.descriptor.tabSheetID === 'ContractAlerts') {
      const ref: ComponentRef<ContractAlertsComponent> = this.rowsContainer.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(ContractAlertsComponent));
      this.contractAlertsComponent = ref.instance;
    }

    if (this.descriptor.tabSheetID === 'PersonMasterDataAdmin') {
      const ref: ComponentRef<PersonMasterDataAdminComponent> = this.rowsContainer.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(PersonMasterDataAdminComponent));
      this.personMasterDataAdminComponent = ref.instance;
    }

    if (this.descriptor.tabSheetID === 'PayrollInputAdmin') {
      const ref: ComponentRef<PayrollInputAdminComponent> = this.rowsContainer.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(PayrollInputAdminComponent));
      this.payrollInputAdminComponent = ref.instance;
    }

    if (this.descriptor.tabSheetID === 'SdcPersonForm') {
      const ref: ComponentRef<SdcPersonFormComponent> = this.rowsContainer.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(SdcPersonFormComponent));
      this.sdcPersonFormComponent = ref.instance;
    }

    this.loadCardsRows().then(controlGroup => this.loaded.emit(controlGroup));
  }

  public ngOnDestroy() {
    this.rows.forEach((rowRef: ComponentRef<FormTabRowComponent>) => {
      rowRef.destroy();
      rowRef = null;
    });
    this.rows = [];
  }

  /**
   * Load the card rows inside this tab. Splits cards by rows of an accumulated four columns, then load cards inside
   * those rows.
   *
   * @returns {Promise<UntypedFormGroup>} a promise of the control group associated to this tab (i.e. containing control
   * groups of all cards in all rows of this tab).
   */
  private loadCardsRows(): Promise<UntypedFormGroup> {
    this.ngOnDestroy();

    const groupZones = (this.descriptor.groupZones || [])
      .sort((a: GroupZoneDescriptor, b: GroupZoneDescriptor) => a.displayorder - b.displayorder);

    // First, group cards by row. A row holds a maximum of four columns.

    const rows: Array<Array<GroupZoneDescriptor>> =
      groupZones.reduce((result: Array<Array<GroupZoneDescriptor>>, item: GroupZoneDescriptor) => {
        const lastRowItems =
          result[result.length - 1] === undefined ? 0 :
            result[result.length - 1].reduce((a: number, zone: GroupZoneDescriptor) => a + zone.nbCol, 0);

        if (result.length === 0 || 4 < item.nbCol + lastRowItems) {
          // If first row or if this item will make the current row overflow , we create a new row
          result.push([]);
        }
        // Push card in current row
        result[result.length - 1].push(item);
        return result;
      }, []);

    // Then create rows component and load corresponding cards in them.
    return Promise.all(rows.map((row: Array<GroupZoneDescriptor>) => {
      const rowRef: ComponentRef<FormTabRowComponent> = this.rowsContainer.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(FormTabRowComponent));
      this.rows.push(rowRef);
      this.cd.markForCheck();
      const cardRow = rowRef.instance;
      cardRow.data = this.data;
      cardRow.zones = row;
      cardRow.readOnly = this.readOnly;
      cardRow.currency = this.currency;
      cardRow.locale = this.locale;
      cardRow.cliRegID = this.cliRegID;
      cardRow.groupedCards = this.groupedCards;
      cardRow.form = this.form;
      cardRow.isOnBoarding = this.isOnBoarding;
      cardRow.employee = this.employee;
      cardRow.tabSheetID = this.descriptor.tabSheetID;
      cardRow.activeCardSubject = this.activeCardSubject;
      cardRow.selectedOrg = this.selectedOrg;
      return new Promise((resolve, reject) => {
        cardRow.loaded.subscribe(value => resolve(value));
        setTimeout(() => rowRef.changeDetectorRef.detectChanges());
      });
    })).then((rowCardGroups: Array<Array<GroupZoneControlGroup>>) => {
      const cardGroups: Array<GroupZoneControlGroup> = [].concat.apply([], rowCardGroups);
      this.group = this.formBuilder.group(cardGroups.reduce((result, item: GroupZoneControlGroup) => {
        result[item.zoneGroup.groupZoneID] = item.controlGroup;
        return result;
      }, {}));
      return this.group;
    });
  }
}
